import {readonly} from 'vue'
import {useRouter} from 'vue-router'
import {useLocalStorage} from '@vueuse/core'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {setLanguage} from '@toolify/client/src/setup/i18n'
import {ClientRouteParamType} from '@toolify/client/src/setup/router/enum/ClientRouteParamType'
import {GUEST_CHOSEN_LOCALE_STORAGE_KEY} from '@toolify/client/src/modules/authentication/constants/guestAppearanceStorageKeys'
import {getPathWithoutLocalePrefix} from '@toolify/client/src/modules/authentication/functions/getPathWithoutLocalePrefix'
import {getPathWithLocalePrefix} from '@toolify/client/src/modules/authentication/functions/getPathWithLocalePrefix'

const chosenLocale = useLocalStorage<LocaleType | null>(GUEST_CHOSEN_LOCALE_STORAGE_KEY, null, {
  writeDefaults: false,
})

/**
 * Moves a signed-out visitor to the same page under another locale prefix. The language they pick here is
 * remembered apart from the per-navigation guest locale, so the locale suggestion banner stops offering
 * a different one.
 */
export function useGuestLocaleSwitch() {
  const router = useRouter()

  function keepLocale(locale: LocaleType) {
    chosenLocale.value = locale
  }

  async function switchLocale(locale: LocaleType) {
    keepLocale(locale)
    setLanguage(locale)
    const route = router.currentRoute.value
    const routeLocale = route.params[ClientRouteParamType.Locale]
    const path = typeof routeLocale === 'string' && routeLocale
      ? getPathWithoutLocalePrefix(route.path, routeLocale)
      : route.path
    await router.replace({
      path: getPathWithLocalePrefix(path, locale),
      query: route.query,
      hash: route.hash,
    })
  }

  return {
    chosenLocale: readonly(chosenLocale),
    keepLocale,
    switchLocale,
  }
}
